import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { useConfirm } from "../../ui/components/ConfirmDialog";

type DeviceRow = {
  deviceId: string;
  shortId?: string;
  status: string;
  owner?: string | { username?: string };
  panelProfile?: string;
  layoutRevision?: number;
  firmwareVersion?: string | null;
  lastSeen?: string | null;
};

function isOnline(d: DeviceRow): boolean {
  return !!(d.lastSeen && Date.now() - new Date(d.lastSeen).getTime() < 5 * 60 * 1000);
}

function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const t = typeof date === "string" ? new Date(date).getTime() : date.getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function formatOwner(owner: DeviceRow["owner"]): string {
  if (!owner) return "—";
  if (typeof owner === "string") return owner;
  return owner.username ?? "—";
}

function mapCreatedDeviceToRow(data: Record<string, unknown>): DeviceRow {
  return {
    deviceId: String(data.deviceId ?? ""),
    shortId: data.shortId as string | undefined,
    status: String(data.status ?? "unregistered"),
    owner: data.owner as DeviceRow["owner"],
    panelProfile: data.panelProfile as string | undefined,
    layoutRevision: typeof data.layoutRevision === "number" ? data.layoutRevision : undefined,
    firmwareVersion: (data.firmwareVersion as string | null) ?? null,
    lastSeen: (data.lastSeen as string | null) ?? null,
  };
}

function extractErrorMessage(e: unknown): string {
  if (e && typeof e === "object") {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    const fromBody = ax.response?.data?.error;
    if (typeof fromBody === "string" && fromBody) return fromBody;
    if (typeof ax.message === "string") return ax.message;
  }
  return "request failed";
}

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  @media (max-width: 700px) {
    padding: 12px 8px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px 0 #232323;
  padding: 1.25rem;
  @media (max-width: 700px) {
    padding: 1rem 0.85rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #444;
  @media (max-width: 700px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
  }
  & > span {
    @media (min-width: 701px) {
      margin-left: 0.65rem;
    }
  }
`;

const BackLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 700;
  color: #232323;
  text-decoration: underline;
  &:hover {
    color: #000;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  @media (min-width: 701px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatTile = styled.div`
  background: #fff;
  border: 1px solid #232323;
  box-shadow: 6px 6px 0 #232323;
  padding: 0.85rem 1rem;
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #555;
`;

const StatNum = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0.35rem 0 0.15rem;
`;

const StatHint = styled.div`
  font-size: 0.65rem;
  color: #777;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 800;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.45rem 0.5rem;
  border: 1px solid #232323;
  font-family: inherit;
  font-size: 0.85rem;
  box-shadow: 3px 3px 0 #232323;
  box-sizing: border-box;
  @media (max-width: 700px) {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }
`;

const PrimaryBtn = styled.button`
  padding: 0.45rem 0.85rem;
  border: 1px solid #232323;
  background: #ffd24a;
  color: #232323;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: 3px 3px 0 #232323;
  &:hover:not(:disabled) {
    background: #ffdf73;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  @media (max-width: 700px) {
    width: 100%;
  }
`;

const Explainer = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.72rem;
  color: #555;
  line-height: 1.4;
`;

const HintLine = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  color: #555;
`;

const FlashOk = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #15803d;
  font-weight: 700;
`;

const InlineErr = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: crimson;
`;

const FullListLink = styled(Link)`
  display: inline-block;
  margin-bottom: 0.75rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #232323;
  background: #e8e8e8;
  color: #232323;
  font-weight: 800;
  font-size: 0.85rem;
  text-decoration: none;
  box-shadow: 4px 4px 0 #232323;
  &:hover {
    background: #ddd;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 700px) {
    display: none;
    margin: 0 -8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  th,
  td {
    border: 1px solid #232323;
    padding: 0.4rem 0.45rem;
    text-align: left;
    vertical-align: middle;
  }
  th {
    background: #fafafa;
    font-weight: 700;
  }
  tbody tr:nth-child(even) {
    background: #fafafa;
  }
`;

const MobileCards = styled.div`
  display: none;
  flex-direction: column;
  gap: 0.75rem;
  @media (max-width: 700px) {
    display: flex;
  }
`;

const MobileCard = styled.div`
  border: 1px solid #232323;
  background: #fafafa;
  padding: 0.65rem 0.75rem;
  box-shadow: 4px 4px 0 #232323;
  font-size: 0.78rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const MobileRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MobileLabel = styled.span`
  color: #666;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const OnlineDot = styled.span<{ $online: boolean }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => (p.$online ? "#22c55e" : "#9ca3af")};
  border: 1px solid #232323;
`;

const ShortBig = styled.span`
  font-size: 1.1rem;
  font-weight: 800;
  text-transform: uppercase;
`;

const StatusPill = styled.span<{ $registered: boolean }>`
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  border: 1px solid #232323;
  background: ${(p) => (p.$registered ? "#dcfce7" : "#e5e7eb")};
  color: #232323;
`;

const BtnLink = styled(Link)`
  display: inline-block;
  padding: 0.28rem 0.55rem;
  border: 1px solid #232323;
  background: #fff;
  color: #232323;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.72rem;
  box-shadow: 2px 2px #232323;
  margin-right: 0.35rem;
  &:hover {
    background: #e8e8e8;
  }
`;

const RevokeBtn = styled.button`
  padding: 0.28rem 0.55rem;
  border: 1px solid #232323;
  background: #fff;
  color: #232323;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.72rem;
  cursor: pointer;
  box-shadow: 2px 2px #232323;
  &:hover:not(:disabled) {
    background: #ffe4e4;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DocBlurb = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: #666;
  line-height: 1.45;
  max-width: 52rem;
`;

const Err = styled.p`
  color: crimson;
  margin: 0 0 0.75rem;
`;

export default function AotdIrlAdminDashboard() {
  const auth = useAppSelector((s) => s.auth);
  const confirm = useConfirm();
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newDeviceId, setNewDeviceId] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createOk, setCreateOk] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const firstFetchRef = useRef(true);

  const loadDevices = useCallback(async () => {
    try {
      const resp = await apiClient.get<{ devices?: DeviceRow[] }>("/api/aotd-irl/admin/devices");
      setDevices(resp.data?.devices ?? []);
      setLastUpdatedAt(Date.now());
      setError(null);
    } catch (e: unknown) {
      const msg = extractErrorMessage(e);
      if (firstFetchRef.current) {
        setError(msg);
      }
    } finally {
      if (firstFetchRef.current) {
        firstFetchRef.current = false;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState === "visible") void loadDevices();
    };
    const id = window.setInterval(poll, 5000);
    const onVis = () => {
      if (document.visibilityState === "visible") void loadDevices();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadDevices]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!createOk) return;
    const t = window.setTimeout(() => setCreateOk(false), 3000);
    return () => window.clearTimeout(t);
  }, [createOk]);

  const stats = useMemo(() => {
    const total = devices.length;
    const registered = devices.filter((d) => d.status === "registered").length;
    const online = devices.filter((d) => isOnline(d)).length;
    const unclaimed = devices.filter((d) => d.status !== "registered").length;
    return { total, registered, online, unclaimed };
  }, [devices]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newDeviceId.trim();
    if (!trimmed) {
      setCreateErr("deviceId is required");
      return;
    }
    setCreating(true);
    setCreateErr(null);
    setCreateOk(false);
    try {
      const resp = await apiClient.post<{ device?: Record<string, unknown> }>(
        "/api/aotd-irl/admin/devices",
        { deviceId: trimmed }
      );
      const raw = resp.data?.device;
      if (raw && typeof raw === "object") {
        setDevices((prev) => [mapCreatedDeviceToRow(raw), ...prev.filter((d) => d.deviceId !== raw.deviceId)]);
      }
      setNewDeviceId("");
      setCreateOk(true);
      setLastUpdatedAt(Date.now());
    } catch (err: unknown) {
      setCreateErr(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (deviceId: string) => {
    const ok = await confirm({
      title: "Revoke device token?",
      message: "The device will need to re-pair before it can sync again.",
      confirmLabel: "Revoke",
      tone: "danger",
    });
    if (!ok) return;
    setRevokingId(deviceId);
    try {
      await apiClient.post(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/revoke-token`);
      toast.success("token revoked", { position: toast.POSITION.BOTTOM_LEFT });
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err), { position: toast.POSITION.BOTTOM_LEFT });
    } finally {
      setRevokingId(null);
    }
  };

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }

  const secsSinceUpdate = Math.max(0, Math.floor((now - lastUpdatedAt) / 1000));

  const renderDeviceCells = (d: DeviceRow) => {
    const online = isOnline(d);
    const reg = d.status === "registered";
    return (
      <>
        <td>
          <OnlineDot $online={online} title={online ? "online" : "offline"} />
        </td>
        <td>
          <ShortBig>{(d.shortId ?? "—").toString().toUpperCase()}</ShortBig>
        </td>
        <td>{formatOwner(d.owner)}</td>
        <td>
          <StatusPill $registered={reg}>{d.status}</StatusPill>
        </td>
        <td>
          {`${d.panelProfile ?? "—"} · r${d.layoutRevision ?? 0}`}
        </td>
        <td>{d.firmwareVersion ?? "—"}</td>
        <td>{formatRelative(d.lastSeen)}</td>
        <td>
          <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}`}>Manage</BtnLink>
          <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}/layout`}>Edit layout</BtnLink>
          <RevokeBtn
            type="button"
            disabled={revokingId === d.deviceId}
            onClick={() => handleRevoke(d.deviceId)}
          >
            Revoke token
          </RevokeBtn>
        </td>
      </>
    );
  };

  return (
    <Wrap>
      <Inner>
        <Card>
          <HeaderRow>
            <BackLink to="/admin">← admin</BackLink>
            <Title>AOTD-IRL admin</Title>
            <Subtitle>
              frames, hardware, stats
              <span
                style={{
                  marginLeft: 0,
                  color: "#555",
                  fontSize: "0.78rem",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#22c55e",
                    border: "1px solid #232323",
                    display: "inline-block",
                  }}
                  title="live"
                />
                · updated {secsSinceUpdate}s ago
              </span>
            </Subtitle>
          </HeaderRow>
        </Card>

        <StatsGrid>
          <StatTile>
            <StatLabel>Total frames</StatLabel>
            <StatNum>{stats.total}</StatNum>
            <StatHint>all devices</StatHint>
          </StatTile>
          <StatTile>
            <StatLabel>Registered</StatLabel>
            <StatNum>{stats.registered}</StatNum>
            <StatHint>status registered</StatHint>
          </StatTile>
          <StatTile>
            <StatLabel>Online now</StatLabel>
            <StatNum>{stats.online}</StatNum>
            <StatHint>seen in 5 min</StatHint>
          </StatTile>
          <StatTile>
            <StatLabel>Unclaimed</StatLabel>
            <StatNum>{stats.unclaimed}</StatNum>
            <StatHint>not registered</StatHint>
          </StatTile>
        </StatsGrid>

        <Card>
          <SectionTitle>Create new frame</SectionTitle>
          <form onSubmit={handleCreate}>
            <FormRow>
              <TextInput
                value={newDeviceId}
                onChange={(ev) => setNewDeviceId(ev.target.value)}
                placeholder="paste firmware-generated deviceId or a new unique id"
                aria-label="device id"
                autoComplete="off"
              />
              <PrimaryBtn type="submit" disabled={creating}>
                Create frame
              </PrimaryBtn>
            </FormRow>
          </form>
          <Explainer>
            creates a pre-registered device record. the physical esp must still sync to claim its token and get bound to an
            owner.
          </Explainer>
          <HintLine>
            Need many frames at once?{" "}
            <Link to="/aotd-irl/admin/provision" style={{ fontWeight: 700, color: "#232323", textDecoration: "underline" }}>
              Open the provisioning page →
            </Link>
          </HintLine>
          <HintLine>
            Managing panel hardware profiles?{" "}
            <Link
              to="/aotd-irl/admin/display-templates"
              style={{ fontWeight: 700, color: "#232323", textDecoration: "underline" }}
            >
              Open display templates →
            </Link>
          </HintLine>
          {createOk && <FlashOk>created</FlashOk>}
          {createErr && <InlineErr>{createErr}</InlineErr>}
        </Card>

        <Card>
          <FullListLink to="/aotd-irl/admin/devices">Open full devices list</FullListLink>
          <SectionTitle>Devices</SectionTitle>
          {loading && <p style={{ margin: 0 }}>Loading…</p>}
          {error && <Err>{error}</Err>}
          {!loading && !error && (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th />
                      <th>shortId</th>
                      <th>owner</th>
                      <th>status</th>
                      <th>panel / rev</th>
                      <th>firmware</th>
                      <th>last seen</th>
                      <th>actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.deviceId}>{renderDeviceCells(d)}</tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              <MobileCards>
                {devices.map((d) => {
                  const online = isOnline(d);
                  const reg = d.status === "registered";
                  return (
                    <MobileCard key={d.deviceId}>
                      <MobileRow>
                        <span>
                          <OnlineDot $online={online} />{" "}
                          <ShortBig>{(d.shortId ?? "—").toString().toUpperCase()}</ShortBig>
                        </span>
                        <StatusPill $registered={reg}>{d.status}</StatusPill>
                      </MobileRow>
                      <MobileRow>
                        <MobileLabel>owner</MobileLabel>
                        <span>{formatOwner(d.owner)}</span>
                      </MobileRow>
                      <MobileRow>
                        <MobileLabel>panel / rev</MobileLabel>
                        <span>{`${d.panelProfile ?? "—"} · r${d.layoutRevision ?? 0}`}</span>
                      </MobileRow>
                      <MobileRow>
                        <MobileLabel>firmware</MobileLabel>
                        <span>{d.firmwareVersion ?? "—"}</span>
                      </MobileRow>
                      <MobileRow>
                        <MobileLabel>last seen</MobileLabel>
                        <span>{formatRelative(d.lastSeen)}</span>
                      </MobileRow>
                      <div style={{ marginTop: 4 }}>
                        <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}`}>Manage</BtnLink>
                        <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}/layout`}>
                          Edit layout
                        </BtnLink>
                        <RevokeBtn
                          type="button"
                          disabled={revokingId === d.deviceId}
                          onClick={() => handleRevoke(d.deviceId)}
                        >
                          Revoke token
                        </RevokeBtn>
                      </div>
                    </MobileCard>
                  );
                })}
              </MobileCards>
            </>
          )}
        </Card>

        <Card>
          <DocBlurb>
            ESP connection is polled via <code>/api/aotd-irl/sync</code>. <code>online</code> means the device checked in
            within the last 5 minutes. Firmware versions come from the <code>x-firmware-version</code> header on sync. To
            bind a device to a user, the user claims via QR after the ESP boots.
          </DocBlurb>
        </Card>
      </Inner>
    </Wrap>
  );
}
